import { Box, Button, Divider, List } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useTranslation } from 'react-i18next';
import { useClearRefinements, useInstantSearch } from 'react-instantsearch';
import ClearAllButton from '../common/components/ClearAllButton';
import Sheet from '../common/components/Sheet';
import useOpenable from '../hooks/useOpenable';
import ClassificationTagFilter from '../discovery/as-buyer/filters/ClassificationTagFilter';
import ClassificationTagFilterTags from '../discovery/as-buyer/filters/ClassificationTagFilterTags';
import { CLASSIFICATION_TAG_SHEET_FILTERS, FAMILY_LEVEL_FILTERS, MAX_TAG_PILLS } from '../discovery/as-buyer/filters/classificationTagFilters';
import { familyLevelLabelKey } from '../discovery/navigation/family-level-facets';

const TAG_ATTRIBUTES = CLASSIFICATION_TAG_SHEET_FILTERS.map(({ attribute }) => attribute);
const FILTER_ATTRIBUTES = [
  ...FAMILY_LEVEL_FILTERS.map(({ attribute }) => attribute),
  ...TAG_ATTRIBUTES,
];

export default function CompanyClassificationTagFilters(): React.JSX.Element {
  const { t } = useTranslation(['common', 'company', 'search', 'product-classification', 'product-classification-tags']);
  const tagItemLabel = (value: string): string => t(`product-classification-tags:${value}`);
  const { isOpen, open, close } = useOpenable();
  const { indexRenderState } = useInstantSearch();
  const { refine: clearTagRefinements, canRefine: canClearTags } = useClearRefinements({ includedAttributes: FILTER_ATTRIBUTES });
  const isRepresented = (attribute: string): boolean => (
    !!indexRenderState.refinementList?.[attribute]?.canRefine
  );
  const representedTags = CLASSIFICATION_TAG_SHEET_FILTERS.filter(({ attribute }) => isRepresented(attribute));

  const showAllFilters = representedTags.length > MAX_TAG_PILLS;

  const visibleTags = showAllFilters ? representedTags.slice(0, MAX_TAG_PILLS) : representedTags;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, overflow: 'auto' }}>
        {showAllFilters && (
          <Button
            variant="text"
            startIcon={<TuneIcon />}
            onClick={open}
            sx={{ flexShrink: 0 }}
          >
            {t('search:all_filters')}
          </Button>
        )}
        {FAMILY_LEVEL_FILTERS.map(({ attribute, labelKey, depth }) => (
          <ClassificationTagFilter
            key={attribute}
            attribute={attribute}
            label={t(labelKey)}
            mode="POPOVER"
            getItemLabel={(code) => t(familyLevelLabelKey(code, depth))}
          />
        ))}
        {visibleTags.map(({ attribute, labelKey }) => (
          <ClassificationTagFilter
            key={attribute}
            attribute={attribute}
            label={t(labelKey)}
            mode="POPOVER"
            getItemLabel={tagItemLabel}
          />
        ))}
      </Box>
      {canClearTags && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {FAMILY_LEVEL_FILTERS.map(({ attribute, depth }) => (
            <ClassificationTagFilterTags
              key={attribute}
              attribute={attribute}
              getItemLabel={(code) => t(familyLevelLabelKey(code, depth))}
            />
          ))}
          {TAG_ATTRIBUTES.map((attribute) => (
            <ClassificationTagFilterTags key={attribute} attribute={attribute} getItemLabel={tagItemLabel} />
          ))}
          <ClearAllButton sx={{ height: 36 }} onClick={() => clearTagRefinements()} />
        </Box>
      )}
      <Sheet
        keepMounted
        open={isOpen}
        onClose={close}
        width={320}
        title={t('common:filters')}
        titleIcon={<TuneOutlinedIcon />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <List
            sx={{
              pt: 1.5,
              flexGrow: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            {FAMILY_LEVEL_FILTERS.map(({ attribute, labelKey, depth }) => (
              <ClassificationTagFilter
                key={attribute}
                attribute={attribute}
                label={t(labelKey)}
                mode="ACCORDION"
                getItemLabel={(code) => t(familyLevelLabelKey(code, depth))}
              />
            ))}
            {CLASSIFICATION_TAG_SHEET_FILTERS.map(({ attribute, labelKey }) => (
              <ClassificationTagFilter
                key={attribute}
                attribute={attribute}
                label={t(labelKey)}
                mode="ACCORDION"
                getItemLabel={tagItemLabel}
              />
            ))}
          </List>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, pb: 1 }}>
            <Button variant="text" onClick={close}>
              {t('common:cancel')}
            </Button>
            <Button variant="contained" onClick={close}>
              {t('common:validate')}
            </Button>
          </Box>
        </Box>
      </Sheet>
    </Box>
  );
}
